/**
 * A link that cannot be used: expired (with self-service "Send a new link" /
 * "Ask for a new invitation"), revoked, already used, unknown, or the
 * People API unreachable. Design: ob-entry.jsx ScreenExpired (activation)
 * and ob-admin.jsx EmpExpired (member). The design's "Contact your account
 * manager" button only raised a toast — there is no such channel — so it is
 * left out.
 */
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CircleCheck, LogIn, RotateCw, Send, Unlink, WifiOff } from "lucide-react";
import {
  Btn,
  Note,
  ObShell,
  TopBar,
  obErrorCode,
  obToast,
  requestNewInvitation,
  type InvitationErrorBody,
  type InvitationKind,
} from "../kit";
import { StatusCard } from "./common";

export type LinkProblemCode = "expired" | "revoked" | "accepted" | "invalid" | "missing_token" | "network";

export interface LinkProblemScreenProps {
  code: LinkProblemCode;
  token: string;
  /** The extra keys validate/accept return for revoked/accepted/expired. */
  body?: InvitationErrorBody | null;
  /** True when a session exists (e.g. the page was reloaded after signing in). */
  authenticated?: boolean;
  /** "Try again" after a network failure. */
  onRetry?: () => void;
}

type Sent = { kind: "resent"; to: string } | { kind: "notified"; inviter: string };

export function LinkProblemScreen({ code, token, body, authenticated, onRetry }: LinkProblemScreenProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sent, setSent] = useState<Sent | null>(null);
  const [busy, setBusy] = useState(false);
  const [requestErr, setRequestErr] = useState<string | null>(null);

  const kind: InvitationKind = body?.kind === "activation" ? "activation" : "member";
  const inviter = body?.inviter_name || "";
  const masked = body?.personal_email_masked || "";

  const signInBtn = (
    <Btn size="l" icon={LogIn} onClick={() => navigate(authenticated ? "/" : "/login")}>
      {authenticated
        ? t("onboarding.invitee.link.openSuite", "Open We-Co Suite")
        : t("onboarding.invitee.signIn", "Sign in")}
    </Btn>
  );

  const requestNew = async () => {
    setBusy(true);
    setRequestErr(null);
    try {
      const res = await requestNewInvitation(token);
      if (res.action === "resent") {
        setSent({ kind: "resent", to: res.sent_to });
        obToast.success(t("onboarding.invitee.link.resentToast", "New activation link sent"));
      } else {
        setSent({ kind: "notified", inviter: res.inviter_name });
        obToast.success(
          t("onboarding.invitee.link.notifiedToast", { defaultValue: "Request sent to {{name}}", name: res.inviter_name }),
        );
      }
    } catch (err) {
      const c = obErrorCode(err);
      setRequestErr(
        c === "rate_limited"
          ? t("onboarding.invitee.link.errRateLimited", "You've asked a few times already. Try again in an hour.")
          : c === "no_inviter"
            ? t(
                "onboarding.invitee.link.errNoInviter",
                "The person who invited you no longer has an account. Contact your organization's administrator directly.",
              )
            : c === "email_failed"
              ? t("onboarding.invitee.link.errEmailFailed", "We couldn't send the email just now. Try again in a few minutes.")
              : c === "accepted"
                ? t("onboarding.invitee.link.errAccepted", "This invitation was already used. Sign in instead.")
                : c === "revoked"
                  ? t("onboarding.invitee.link.errRevoked", "This invitation was withdrawn, so it can't be renewed.")
                  : t("onboarding.invitee.err.generic", "Check your connection and try again."),
      );
    } finally {
      setBusy(false);
    }
  };

  let screen: ReactNode;

  if (code === "expired" && sent) {
    screen =
      sent.kind === "resent" ? (
        <StatusCard tone="ok" icon={Send} title={t("onboarding.invitee.link.checkInbox", "Check your inbox.")} label="Activation link resent">
          <p className="ob-p">
            {t("onboarding.invitee.link.resentBody", {
              defaultValue: "We sent a new activation link to {{email}}. It works once and lasts 7 days.",
              email: sent.to,
            })}
          </p>
        </StatusCard>
      ) : (
        <StatusCard tone="ok" icon={CircleCheck} title={t("onboarding.invitee.link.requestSent", "Request sent.")} label="New invitation requested">
          <p className="ob-p">
            {t("onboarding.invitee.link.notifiedBody", {
              defaultValue: "We let {{name}} know. Your new invitation will arrive by email.",
              name: sent.inviter,
            })}
          </p>
        </StatusCard>
      );
  } else if (code === "expired" && kind === "activation") {
    screen = (
      <StatusCard
        icon={Unlink}
        title={t("onboarding.invitee.link.activationExpiredTitle", "This activation link has expired.")}
        label="Activation link expired"
        actions={
          <Btn size="l" busy={busy} onClick={requestNew}>
            {t("onboarding.invitee.link.sendNewLink", "Send a new link")}
          </Btn>
        }
      >
        <p className="ob-p">
          {masked
            ? t("onboarding.invitee.link.activationExpiredBody", {
                defaultValue: "Activation links work once and last 7 days. Request a new one and we'll send it to {{email}}.",
                email: masked,
              })
            : t(
                "onboarding.invitee.link.activationExpiredBodyNoEmail",
                "Activation links work once and last 7 days. Request a new one and we'll send it to the address on file.",
              )}
        </p>
        {requestErr && <Note kind="bad">{requestErr}</Note>}
      </StatusCard>
    );
  } else if (code === "expired") {
    screen = (
      <StatusCard
        icon={Unlink}
        title={t("onboarding.invitee.link.memberExpiredTitle", "This invitation has expired.")}
        label="Invitation expired"
        actions={
          <Btn size="l" busy={busy} onClick={requestNew}>
            {t("onboarding.invitee.link.askNew", "Ask for a new invitation")}
          </Btn>
        }
      >
        <p className="ob-p">
          {inviter
            ? t("onboarding.invitee.link.memberExpiredBody", {
                defaultValue: "Invitation links last 7 days. Ask {{name}}, your administrator, to send a new one.",
                name: inviter,
              })
            : t(
                "onboarding.invitee.link.memberExpiredBodyNoName",
                "Invitation links last 7 days. Ask your administrator to send a new one.",
              )}
        </p>
        {requestErr && <Note kind="bad">{requestErr}</Note>}
      </StatusCard>
    );
  } else if (code === "revoked") {
    screen = (
      <StatusCard icon={Unlink} title={t("onboarding.invitee.link.revokedTitle", "This invitation was withdrawn.")} label="Invitation revoked">
        <p className="ob-p">
          {inviter
            ? t("onboarding.invitee.link.revokedBody", {
                defaultValue: "The link no longer works. If you still need access, ask {{name}} for a new invitation.",
                name: inviter,
              })
            : t(
                "onboarding.invitee.link.revokedBodyNoName",
                "The link no longer works. If you still need access, ask your administrator for a new invitation.",
              )}
        </p>
      </StatusCard>
    );
  } else if (code === "accepted") {
    screen = (
      <StatusCard
        tone="ok"
        icon={CircleCheck}
        title={t("onboarding.invitee.link.acceptedTitle", "This link has already been used.")}
        label="Invitation already used"
        actions={signInBtn}
      >
        <p className="ob-p">
          {t("onboarding.invitee.link.acceptedBody", "Your account is set up. Sign in with the password you created.")}
        </p>
      </StatusCard>
    );
  } else if (code === "network") {
    screen = (
      <StatusCard
        icon={WifiOff}
        title={t("onboarding.invitee.link.networkTitle", "We couldn't check this link.")}
        label="Validation failed"
        actions={
          <Btn size="l" icon={RotateCw} onClick={onRetry}>
            {t("onboarding.invitee.tryAgain", "Try again")}
          </Btn>
        }
      >
        <p className="ob-p">{t("onboarding.invitee.err.generic", "Check your connection and try again.")}</p>
      </StatusCard>
    );
  } else {
    screen = (
      <StatusCard
        tone="bad"
        icon={Unlink}
        title={t("onboarding.invitee.link.invalidTitle", "This link isn't valid.")}
        label="Invalid invitation"
        actions={signInBtn}
      >
        <p className="ob-p">
          {t(
            "onboarding.invitee.link.invalidBody",
            "Make sure you opened the whole link from your email. If it still doesn't work, ask your administrator for a new invitation.",
          )}
        </p>
      </StatusCard>
    );
  }

  return (
    <ObShell>
      <TopBar />
      {screen}
    </ObShell>
  );
}
